import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  useGetAiChatBotHistoryQuery,
  useSendAiChatBotMessageMutation,
  useFlagAiChatBotMessageMutation,
  useStartNewAiChatBotConversationMutation,
  useGetMyAiChatBotConversationsQuery,
  useDeleteAiChatBotConversationMutation,
  useLazyGetAiChatBotConversationDetailQuery,
} from "../../../app/services/book/aiChatBot"
import Avatar from "../Chatbox/Avatar"

const formatTime = (value) => {
  const d = value ? new Date(value) : new Date()
  if (isNaN(d.getTime())) return ""
  const pad = (n) => String(n).padStart(2, "0")
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} | ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const INLINE_MARKDOWN = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|\*\*([^*]+)\*\*|(https?:\/\/[^\s)<]+)/g
const MARKDOWN_ESCAPE = /\\([\\`*_{}[\]()#+\-.!|>~])/g
const HEADING = /^#{1,6}\s+(.*)$/

const unescapeMarkdown = (text) => text.replace(MARKDOWN_ESCAPE, "$1")

const renderMarkdown = (text) =>
  text.split("\n").flatMap((line, index) => {
    const heading = line.match(HEADING)
    const content = heading ? (
      <strong key={`h${index}`} className="text-slate-900">
        {renderInline(heading[1])}
      </strong>
    ) : (
      renderInline(line)
    )
    return index === 0 ? [content] : ["\n", content]
  })

const CURRENCY_SYMBOLS = { EUR: "€", USD: "$", UAH: "₴", MDL: "L", RON: "lei " }

const formatAmount = (value) =>
  String(Math.round(Number(value))).replace(/\B(?=(\d{3})+(?!\d))/g, " ")

const renderInline = (text) => {
  const parts = []
  let last = 0
  let match

  INLINE_MARKDOWN.lastIndex = 0
  while ((match = INLINE_MARKDOWN.exec(text)) !== null) {
    if (match.index > last) parts.push(unescapeMarkdown(text.slice(last, match.index)))

    const [whole, label, labelUrl, bold, bareUrl] = match
    const url = labelUrl || bareUrl
    if (url) {
      parts.push(
        <a
          key={match.index}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline hover:text-blue-800 break-all"
        >
          {label || url}
        </a>
      )
    } else if (bold) {
      parts.push(<strong key={match.index}>{unescapeMarkdown(bold)}</strong>)
    } else {
      parts.push(whole)
    }
    last = match.index + whole.length
  }

  if (last < text.length) parts.push(unescapeMarkdown(text.slice(last)))
  return parts
}

const OfferCard = ({ offer }) => {
  const { t } = useTranslation()
  const [minNights, maxNights] = offer.requested_nights || []
  const nights = Number(offer.nights)
  const nightsDiffer = minNights != null && (nights < minNights || nights > maxNights)
  const clientSymbol = CURRENCY_SYMBOLS[offer.client_currency] || `${offer.client_currency || ""} `

  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-white shadow-sm">
      <div className="font-semibold text-slate-800">{offer.hotel_name || "—"}</div>
      <div className="text-sm text-slate-500">
        {[offer.resort_name, offer.star_name].filter(Boolean).join(" · ")}
      </div>
      <div className="text-sm text-slate-500">
        {offer.from_date} — {offer.to_date}{" "}
        <span className={nightsDiffer ? "text-orange-600 font-medium" : ""}>
          ({offer.nights} {t("admin.ai_chat_bot_nights", "nights")})
        </span>
      </div>
      <div className="mt-1 flex items-center justify-between gap-2">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-medium text-slate-900">
            {offer.price != null ? `${formatAmount(offer.price)} ${offer.currency || ""}`.trim() : "—"}
          </span>
          {offer.client_price != null && offer.client_currency !== offer.currency && (
            <span className="text-sm text-slate-500">
              ≈ {clientSymbol}
              {formatAmount(offer.client_price)}
            </span>
          )}
          {offer.within_budget === true && (
            <span className="text-[11px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">
              {t("admin.ai_chat_bot_in_budget", "in budget")}
            </span>
          )}
          {offer.within_budget === false && (
            <span className="text-[11px] px-1.5 py-0.5 rounded bg-orange-50 text-orange-700">
              {t("admin.ai_chat_bot_over_budget", "over budget")}
            </span>
          )}
        </div>
        {offer.book_url && (
          <a
            href={offer.book_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-medium px-2.5 py-1 rounded-md bg-blue-600 text-white hover:bg-blue-700"
          >
            {t("admin.ai_chat_bot_book", "Book")}
          </a>
        )}
      </div>
    </div>
  )
}

const FlagControl = ({ message, onFlag }) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [comment, setComment] = useState("")
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState(false)

  if (message.meta?.flagged) {
    return (
      <span className="text-[11px] text-slate-400 px-1">
        {t("admin.ai_chat_bot_flagged", "Reported as incorrect. Thanks!")}
      </span>
    )
  }

  if (!isOpen) {
    return (
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="text-[11px] text-slate-400 hover:text-rose-600 px-1"
      >
        {t("admin.ai_chat_bot_flag", "Incorrect answer")}
      </button>
    )
  }

  const submit = async () => {
    setIsSaving(true)
    setError(false)
    try {
      await onFlag(message.id, comment.trim())
    } catch (_error) {
      setError(true)
      setIsSaving(false)
    }
  }

  return (
    <div className="flex items-center gap-1.5 px-1 mt-0.5 w-full">
      <input
        type="text"
        value={comment}
        onChange={(event) => setComment(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") submit()
        }}
        placeholder={t("admin.ai_chat_bot_flag_placeholder", "What was wrong? (optional)")}
        className="flex-1 min-w-0 border border-slate-300 rounded px-2 py-1 text-xs"
        autoFocus
      />
      <button
        type="button"
        onClick={submit}
        disabled={isSaving}
        className="text-xs px-2 py-1 rounded bg-rose-600 text-white disabled:opacity-50"
      >
        {t("admin.ai_chat_bot_flag_send", "Report")}
      </button>
      <button type="button" onClick={() => setIsOpen(false)} className="text-xs text-slate-400 px-1">
        ×
      </button>
      {error && <span className="text-[11px] text-rose-600">{t("main.error_occurred", "Something went wrong.")}</span>}
    </div>
  )
}

const MessageBubble = ({ message, canFlag, onFlag }) => {
  const isBot = message.role === "bot"
  const isOwn = !isBot
  const offers =
    message.meta?.offers ||
    (Array.isArray(message.meta?.payload) ? message.meta.payload.filter((offer) => offer?.book_url) : [])

  return (
    <div className={`flex gap-2.5 mb-3 ${isOwn ? "justify-end" : "justify-start"}`}>
      {!isOwn && <Avatar className="w-8 h-8 mt-4" company="AI" />}

      <div className={`flex flex-col max-w-[80%] ${isOwn ? "items-end" : "items-start"}`}>
        <span className="text-xs font-semibold text-slate-500 mb-0.5 px-1">
          {isOwn ? "" : "AI Bot"}
        </span>

        <div
          className={`border shadow-sm px-4 py-2.5 ${
            isOwn ? "bg-orange-50/30 border-orange-200" : "bg-slate-50 border-slate-200"
          }`}
          style={{ borderRadius: isOwn ? "18px 4px 18px 18px" : "4px 18px 18px 18px" }}
        >
          <p className="text-[15px] leading-[1.5] whitespace-pre-wrap break-words text-slate-900">
            {isBot ? renderMarkdown(message.content || "") : message.content}
          </p>

          {offers.length > 0 && (
            <div className="mt-2 grid gap-2">
              {offers.map((offer) => (
                <OfferCard key={`${offer.token}-${offer.price_id}`} offer={offer} />
              ))}
            </div>
          )}
        </div>

        <span className="text-[11px] text-slate-400 mt-0.5 px-1">
          {formatTime(message.created_at)}
        </span>

        {isBot && canFlag && Number.isInteger(message.id) && <FlagControl message={message} onFlag={onFlag} />}
      </div>
    </div>
  )
}

const LOADING_TIPS = [
  ["admin.ai_chat_bot_tip_ram", "Downloading more RAM..."],
  ["admin.ai_chat_bot_tip_isp", "Tip your internet provider..."],
  ["admin.ai_chat_bot_tip_splines", "Reticulating splines..."],
  ["admin.ai_chat_bot_tip_hamsters", "Convincing the hamsters to run faster..."],
  ["admin.ai_chat_bot_tip_multiverse", "Searching every hotel in the multiverse..."],
  ["admin.ai_chat_bot_tip_wifi", "Untangling the WiFi cables..."],
  ["admin.ai_chat_bot_tip_almost", "Almost there, promise..."],
  ["admin.ai_chat_bot_tip_camels", "Negotiating with a camel for a better rate..."],
]

const TypingIndicator = ({ reassuranceTip }) => {
  const { t } = useTranslation()

  return (
    <div className="flex gap-2.5 mb-3 justify-start">
      <Avatar className="w-8 h-8 mt-4" company="AI" />
      <div
        className="border border-slate-200 bg-slate-50 px-4 py-3 flex items-center gap-2"
        style={{ borderRadius: "4px 18px 18px 18px" }}
      >
        <div className="flex items-center gap-1">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce"
              style={{ animationDelay: `${i * 0.12}s` }}
            />
          ))}
        </div>
        {reassuranceTip && (
          <span className="text-xs text-slate-500">
            {t(reassuranceTip[0], reassuranceTip[1])}
          </span>
        )}
      </div>
    </div>
  )
}

const AiChatBotPage = () => {
  const { t } = useTranslation()
  const { data: history, isLoading: isHistoryLoading } = useGetAiChatBotHistoryQuery()
  const { data: myConversations, refetch: refetchMyConversations } = useGetMyAiChatBotConversationsQuery()
  const [sendMessage, { isLoading: isSending }] = useSendAiChatBotMessageMutation()
  const [flagMessage] = useFlagAiChatBotMessageMutation()
  const [startNewConversation, { isLoading: isStartingNew }] = useStartNewAiChatBotConversationMutation()
  const [fetchConversationDetail] = useLazyGetAiChatBotConversationDetailQuery()
  const [deleteConversation, { isLoading: isDeletingConversation }] = useDeleteAiChatBotConversationMutation()
  const [text, setText] = useState("")
  const [messages, setMessages] = useState([])
  const [conversationId, setConversationId] = useState(null)
  const [reassuranceTip, setReassuranceTip] = useState(null)
  const bottomRef = useRef(null)
  const textareaRef = useRef(null)

  useEffect(() => {
    if (history?.messages) {
      setMessages(history.messages)
      setConversationId(history.conversation_id)
    }
  }, [history])

  useEffect(() => {
    if (!isSending) {
      setReassuranceTip(null)
      return
    }

    const timer = setTimeout(() => {
      setReassuranceTip(LOADING_TIPS[Math.floor(Math.random() * LOADING_TIPS.length)])
    }, 4000)
    return () => clearTimeout(timer)
  }, [isSending])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages, isSending])

  const handleSend = async () => {
    const value = text.trim()
    if (!value || isSending) return

    setMessages((prev) => [
      ...prev,
      {
        id: `local-${Date.now()}`,
        role: "user",
        content: value,
        meta: {},
        created_at: new Date().toISOString(),
      },
    ])
    setText("")

    try {
      const result = await sendMessage({ text: value, conversation_id: conversationId }).unwrap()
      setConversationId(result.conversation_id)
      setMessages((prev) => [
        ...prev,
        {
          id: result.message_id || `bot-${Date.now()}`,
          role: "bot",
          content: result.reply,
          meta: { offers: result.offers || [] },
          created_at: new Date().toISOString(),
        },
      ])
      refetchMyConversations()
    } catch (_error) {
      setMessages((prev) => [
        ...prev,
        {
          id: `error-${Date.now()}`,
          role: "bot",
          content: t("main.error_occurred", "Something went wrong."),
          meta: {},
          created_at: new Date().toISOString(),
        },
      ])
    }
  }

  const handleFlag = async (id, comment) => {
    await flagMessage({ id, comment }).unwrap()
    setMessages((prev) =>
      prev.map((message) =>
        message.id === id ? { ...message, meta: { ...message.meta, flagged: true } } : message
      )
    )
  }

  const handleNewChat = async () => {
    try {
      const result = await startNewConversation().unwrap()
      setConversationId(result.conversation_id)
      setMessages([])
      refetchMyConversations()
    } catch (_error) {
      // ignore — user can retry
    }
  }

  const handleSelectConversation = async (id) => {
    if (id === conversationId) return
    try {
      const result = await fetchConversationDetail({ bundle_id: id }).unwrap()
      setConversationId(result.conversation_id)
      setMessages(result.messages)
    } catch (_error) {
      // ignore — user can retry
    }
  }

  const handleDeleteConversation = async (event, id) => {
    event.stopPropagation()
    if (!window.confirm(t("admin.ai_chat_bot_delete_confirm", "Delete this conversation?"))) return

    try {
      await deleteConversation(id).unwrap()
      refetchMyConversations()

      if (id === conversationId) {
        await handleNewChat()
      }
    } catch (_error) {
      // ignore — user can retry
    }
  }

  return (
    <div className="max-w-5xl mx-auto p-4 flex gap-4 h-[80vh]">
      <div className="w-56 shrink-0 flex flex-col border border-slate-200 rounded-lg bg-white overflow-hidden">
        <button
          type="button"
          onClick={handleNewChat}
          disabled={isStartingNew}
          className="m-2 px-3 py-2 rounded-md bg-slate-800 text-white text-sm font-medium disabled:opacity-50"
        >
          + {t("admin.ai_chat_bot_new_chat", "New chat")}
        </button>
        <div className="flex-1 overflow-y-auto px-2 pb-2">
          {(myConversations?.conversations || []).map((conversation) => (
            <div
              key={conversation.id}
              className={`group w-full flex items-center gap-1 px-2 py-2 rounded-md text-xs mb-1 ${
                conversation.id === conversationId
                  ? "bg-blue-50 text-blue-800"
                  : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              <button
                type="button"
                onClick={() => handleSelectConversation(conversation.id)}
                className="flex-1 min-w-0 text-left"
              >
                <div className="truncate font-medium">
                  {conversation.preview || t("admin.ai_chat_bot_new_chat", "New chat")}
                </div>
                <div className="text-[10px] text-slate-400">{formatTime(conversation.updated_at)}</div>
              </button>
              <button
                type="button"
                onClick={(event) => handleDeleteConversation(event, conversation.id)}
                disabled={isDeletingConversation}
                title={t("admin.ai_chat_bot_delete_chat", "Delete")}
                aria-label={t("admin.ai_chat_bot_delete_chat", "Delete")}
                className="shrink-0 w-5 h-5 flex items-center justify-center rounded text-slate-400 opacity-0 group-hover:opacity-100 hover:bg-red-50 hover:text-red-600 disabled:opacity-50 transition-opacity"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex-1 flex flex-col min-w-0">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-lg font-semibold text-slate-800">
            {t("admin.ai_chat_bot_title", "AI Chat Search")}
          </h1>
        </div>

        <div className="flex-1 overflow-y-auto border border-slate-200 rounded-lg p-3 bg-slate-100">
          {isHistoryLoading ? (
            <div className="text-sm text-slate-400">{t("admin.loading_dots")}</div>
          ) : (
            messages.map((message) => (
              <MessageBubble
                key={message.id}
                message={message}
                canFlag={Boolean(history?.flag_button_enabled)}
                onFlag={handleFlag}
              />
            ))
          )}
          {isSending && <TypingIndicator reassuranceTip={reassuranceTip} />}
          <div ref={bottomRef} />
        </div>

        <div className="mt-3 flex gap-2">
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault()
                handleSend()
              }
            }}
            rows={1}
            placeholder={t(
              "admin.ai_chat_bot_placeholder",
              'Ask for a tour, e.g. "2+1, Kemer, 7 nights"'
            )}
            className="flex-1 border border-slate-300 rounded-md px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-[15px] leading-relaxed"
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={isSending || !text.trim()}
            className="px-4 py-2 rounded-md bg-blue-600 text-white disabled:opacity-50 self-end"
          >
            {t("main.send", "Send")}
          </button>
        </div>
      </div>
    </div>
  )
}

export default AiChatBotPage
